import React, { useState, useEffect, useCallback } from 'react';
import OnboardingModal from './OnboardingModal';

const OnboardingManager = ({ user, supabaseHelpers, isEnabled = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Debug: Log when component mounts
  console.log('🎯 OnboardingManager: Component rendered', { 
    user: user ? { id: user.id, sub: user.sub, email: user.email } : null, 
    isEnabled, 
    hasSupabaseHelpers: !!supabaseHelpers 
  });

  // Check for unanswered questions on component mount and user change
  useEffect(() => {
    if (!user || !isEnabled || !supabaseHelpers) {
      console.log('🎯 OnboardingManager: Skipping check', { 
        hasUser: !!user, 
        isEnabled, 
        hasSupabaseHelpers: !!supabaseHelpers 
      });
      setIsLoading(false);
      return;
    }
    
    // Ensure user has both id/sub and email (fully loaded)
    const userId = user.id || user.sub;
    if (!userId || !user.email) {
      console.log('🎯 OnboardingManager: User not fully loaded yet', { userId, email: user.email });
      setIsLoading(false);
      return;
    }
    
    console.log('🎯 OnboardingManager: User fully loaded, checking questions...', { userId, email: user.email });
    
    // Add a small delay to ensure everything is initialized
    const timeoutId = setTimeout(() => {
      checkUnansweredQuestions();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [user?.id, user?.sub, user?.email, isEnabled, supabaseHelpers]);

  const checkUnansweredQuestions = useCallback(async () => {
    console.log('🎯 OnboardingManager: checkUnansweredQuestions called');
    console.log('🎯 OnboardingManager: user:', !!user, user ? { id: user.id, sub: user.sub, email: user.email } : null);
    console.log('🎯 OnboardingManager: supabaseHelpers:', !!supabaseHelpers);
    console.log('🎯 OnboardingManager: getUnansweredOnboardingQuestions function:', !!supabaseHelpers?.getUnansweredOnboardingQuestions);
    
    if (!user || !supabaseHelpers?.getUnansweredOnboardingQuestions) {
      console.warn('🎯 OnboardingManager: Missing user or supabaseHelpers - cannot proceed');
      
      // TODO: Fix Supabase authentication issue to enable proper database queries
      // For now, force show modal for testing (since we know user has no profile yet)
      console.warn('🎯 OnboardingManager: Auth issue detected - forcing modal for testing');
      setUnansweredQuestions(['ideal_saturday', 'learning_inspiration', 'passion_venues']);
      setIsModalOpen(true);
      setIsLoading(false);
      return;
    }

    try {
      const userId = user.id || user.sub; // Handle both id and sub
      console.log('🎯 OnboardingManager: Checking unanswered questions for user:', userId);
      console.log('🎯 OnboardingManager: Full user object:', user);
      
      const { data: unanswered, error } = await supabaseHelpers.getUnansweredOnboardingQuestions(userId);
      
      if (error) {
        console.error('🎯 OnboardingManager: Error checking unanswered questions:', error);
        setIsLoading(false);
        return;
      }

      console.log('🎯 OnboardingManager: Unanswered questions result:', unanswered);
      
      setUnansweredQuestions(unanswered || []);
      
      // Show modal if there are unanswered questions
      if (unanswered && unanswered.length > 0) {
        setIsModalOpen(true);
        console.log('🎯 OnboardingManager: Showing modal for questions:', unanswered);
      } else {
        console.log('🎯 OnboardingManager: No unanswered questions found, unanswered result:', unanswered);
      }
      
      // TEMPORARY DEBUG: Force show modal for testing if user has no profile yet
      if (unanswered && unanswered.length === 3) {
        console.log('🎯 OnboardingManager: New user detected - all 3 questions unanswered');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('🎯 OnboardingManager: Error in checkUnansweredQuestions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, supabaseHelpers]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    console.log('🎯 OnboardingManager: Modal closed by user');
  };

  const handleOnboardingComplete = () => {
    setIsModalOpen(false);
    setUnansweredQuestions([]);
    
    console.log('🎯 OnboardingManager: User completed onboarding session');
    
    // Log completion event (even if partial)
    if (supabaseHelpers?.logEvent) {
      supabaseHelpers.logEvent('onboarding_session_completed', {
        user_id: user.id || user.sub,
        completed_at: new Date().toISOString(),
        questions_in_session: unansweredQuestions.length
      }, user.id);
    }
    
    // Refresh to check for any remaining questions immediately
    // This will either show more questions or hide the modal if all done
    checkUnansweredQuestions();
  };

  // Add a new function to handle single question completion
  const handleQuestionAnswered = (answeredQuestionKey) => {
    console.log('🎯 OnboardingManager: Question answered:', answeredQuestionKey);
    
    // Remove the answered question from the unanswered list
    setUnansweredQuestions(prev => {
      const updated = prev.filter(q => q !== answeredQuestionKey);
      console.log('🎯 OnboardingManager: Updated unanswered questions:', updated);
      
      // If no more questions, close modal completely
      if (updated.length === 0) {
        setIsModalOpen(false);
      }
      
      return updated;
    });
  };

  // Don't render anything if loading or no user
  if (isLoading || !user || !isEnabled) {
    console.log('🎯 OnboardingManager: Not rendering - loading:', isLoading, 'user:', !!user, 'enabled:', isEnabled);
    return null;
  }

  // Don't render modal if no unanswered questions
  if (unansweredQuestions.length === 0) {
    console.log('🎯 OnboardingManager: Not rendering - no unanswered questions');
    return null;
  }

  try {
    return (
      <OnboardingModal
        user={user}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onComplete={handleOnboardingComplete}
        onQuestionAnswered={handleQuestionAnswered}
        supabaseHelpers={supabaseHelpers}
        unansweredQuestions={unansweredQuestions}
      />
    );
  } catch (error) {
    console.error('🎯 OnboardingManager: Error rendering modal:', error);
    return null;
  }
};

export default OnboardingManager; 